/**
 * @module cercaliagl/JaroWinkler
 */

/**
 * @classdesc
 * Implementation from https://github.com/shannonarcher/jarowinkler-js/blob/master/JaroWinkler.js
 */
class JaroWinkler {
  constructor() {
    /**
     * @private
     * @type {number}
     */
    this.p = 0.1;
    /**
     * @private
     * @type {number}
     */
    this.l = 4;
    /**
     * @private
     * @type {number}
     */
    this.bt = 0.7;
  }

  /**
   * Takes two string values and returns a value d
   * between 0 and 1 indicating similarity
   * @param {string} a String to compare
   * @param {string} b String to compare
   * @param {boolean} opt_sensitiveCase `true` if sensitive case. Default
   * @return {number} Distance value
   */
  distance(a, b, sensitiveCase = false) {
    if (sensitiveCase === false) {
      a = a.toLowerCase(); // eslint-disable-line no-param-reassign
      b = b.toLowerCase(); // eslint-disable-line no-param-reassign
    }

    const s1 = a.length;
    const s2 = b.length;
    const m = JaroWinkler.matching(a, b);
    const t = JaroWinkler.transpositions(a, b);
    const p = this.prefix_(a, b);

    if (m > 0) {
      // calculate the jaro distance
      const dj = (m / s1 + m / s2 + (m - t) / m) / 3;
      // return jaro if less than boost
      if (dj < this.bt) {
        return dj;
      }
      // transform to jaro winkler
      return dj + p * this.p * (1 - dj);
    }
    return 0;
  }

  /**
   * Calculate matching characters between strings
   * @private
   * @param {string} a String to compare
   * @param {string} b String to compare
   * @return {number} Number of matches.
   */
  static matching(a, b) {
    const bound = Math.floor(Math.max(a.length, b.length) / 2) - 1;
    const matched = [];
    let matches = 0;

    for (let i = 0; i < a.length; i += 1) {
      for (let j = Math.max(0, i - bound); j <= Math.min(b.length, i + bound); j += 1) {
        if (a[i] === b[j] && !matched[j]) {
          matched[j] = true;
          matches += 1;
          break;
        }
      }
    }

    return matches;
  }

  /**
   * Calculate the number of transpositions between the two words
   * @param {string} a String to compare
   * @param {string} b String to compare
   * @return {number} Number of transpositons.
   */
  static transpositions(a, b) {
    let t = 0;
    let i = 0;
    let j = 0;
    const bound = Math.floor(Math.max(a.length, b.length) / 2) - 1;

    let amatch = '';
    let bmatch = '';
    let matched = [];

    // get order of string matches between each word
    for (i = 0; i < a.length; i += 1) {
      for (j = Math.max(0, i - bound); j <= Math.min(b.length, i + bound); j += 1) {
        if (a[i] === b[j] && !matched[j]) {
          amatch += a[i];
          matched[j] = true;
          break;
        }
      }
    }

    matched = [];
    for (i = 0; i < b.length; i += 1) {
      for (j = Math.max(0, i - bound); j <= Math.min(a.length, i + bound); j += 1) {
        if (b[i] === a[j] && !matched[j]) {
          bmatch += b[i];
          matched[j] = true;
          break;
        }
      }
    }

    // get transpositions
    for (i = 0; i < amatch.length; i += 1) {
      if (amatch[i] !== bmatch[i]) {
        t += 1;
      }
    }

    return Math.floor(t / 2);
  }

  /**
   * Counts the number of common characters at the beginning
   * of each word up to a maximum of 4
   * @private
   * @param {string} a String to compare
   * @param {string} b String to compare
   * @return {number} Prefix position
   */
  prefix_(a, b) {
    let p = 0;
    for (p = 0; p < this.l; p += 1) {
      if (a[p] !== b[p]) {
        return p;
      }
    }
    p += 1;
    return p;
  }
}

export default JaroWinkler;
